import type { SongPlaybackTimeline } from "./songPlayback";
import type { TimedMusicXmlNote } from "./songEngine";

/** Compensación típica mic + YIN + suavizado (ms). */
export const DEFAULT_PITCH_LATENCY_MS = 130;

/** Adelanto visual del playhead en practicar (ms). */
export const PRACTICE_PLAYHEAD_LOOKAHEAD_MS = 85;

export function clampMs(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export interface AdaptiveNoteWindows {
  durationMs: number;
  ioiMs: number;
  earlyMs: number;
  lateMs: number;
  sustainTailMs: number;
  lateGraceMs: number;
}

/** Ventanas de scoring proporcionales al IOI y duración de la figura. */
export function computeAdaptiveNoteWindows(durationMs: number, ioiMs: number): AdaptiveNoteWindows {
  const dur = Math.max(60, durationMs);
  const ioi = Math.max(dur, ioiMs, 70);
  return {
    durationMs: dur,
    ioiMs: ioi,
    earlyMs: clampMs(Math.round(ioi * 0.18), 70, 200),
    lateMs: clampMs(Math.round(ioi * 0.38), 100, 340),
    sustainTailMs: clampMs(Math.round(dur * 0.28), 35, 150),
    lateGraceMs: clampMs(Math.round(ioi * 0.22), 60, 180),
  };
}

export function getEventDurationMs(
  timeline: SongPlaybackTimeline,
  note: TimedMusicXmlNote
): number {
  const seg = timeline.segments.find((s) => s.startTime === note.startTime);
  if (!seg) return 400;
  return Math.max(60, seg.endMs - seg.startMs);
}

export interface LearnDetectionProfile {
  holdTimeMs: number;
  lockMs: number;
  silenceMinMs: number;
  silenceTimeoutMs: number;
}

/** Parámetros de detección en Aprender según duración y si la siguiente nota repite pitch class. */
export function getLearnDetectionProfile(
  durationMs: number,
  samePitchClassNext: boolean,
  eventVoiceCount = 1
): LearnDetectionProfile {
  const dur = Math.max(80, durationMs);
  const isChord = eventVoiceCount >= 2;
  const holdTimeMs = isChord
    ? clampMs(Math.round(dur * 0.07), 6, 22)
    : clampMs(Math.round(dur * 0.2), 22, 95);
  const lockMs = isChord
    ? clampMs(Math.round(dur * 0.18), 45, 110)
    : clampMs(Math.round(dur * 0.32), 100, 200);
  if (samePitchClassNext) {
    return { holdTimeMs, lockMs, silenceMinMs: 0, silenceTimeoutMs: 0 };
  }
  const short = dur < 380;
  return {
    holdTimeMs,
    lockMs,
    silenceMinMs: short ? clampMs(Math.round(dur * 0.1), 0, 35) : 45,
    silenceTimeoutMs: short ? 140 : 200,
  };
}

export function pitchClass(midi: number): number {
  return ((Math.round(midi) % 12) + 12) % 12;
}
